import React, { useState } from "react";
import { Form, Button, Table } from "react-bootstrap";

const affiliateModels = [
    { value: "ONETIME-50", label: "ONETIME-50", period: 1, levels: [0.5, 0, 0] },
    { value: "1Y-20", label: "1Y-20", period: 12, levels: [0.2, 0.1, 0.05] },
    { value: "2Y-15", label: "2Y-15", period: 24, levels: [0.15, 0.075, 0.0375] },
    { value: "3Y-10", label: "3Y-10", period: 36, levels: [0.1, 0.05, 0.025] },
    { value: "5Y-5", label: "5Y-5", period: 60, levels: [0.05, 0.025, 0.0125] },

];

const prices = [9.99, 19.99, 29.99];
const averagePrice = (9.40 + 19.11 + 28.82) / 3; // net average

const getNet = (price) => price - (price * 0.029 + 0.3);

const AffiliateFullComparison = () => {
    const [structure, setStructure] = useState({
        level1: 10,
        level2: 10,
        level3: 10
    });
    const [results, setResults] = useState(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setStructure((prev) => ({ ...prev, [name]: Number(value) }));
    };

    const calculate = () => {
        const modelSummaries = affiliateModels.map((model) => {
            const [l1p, l2p, l3p] = model.levels;
            const period = model.period;

            const calc = (net) => {
                const l1 = structure.level1 * l1p * net * period;
                const l2 = structure.level1 * structure.level2 * l2p * net * period;
                const l3 = structure.level1 * structure.level2 * structure.level3 * l3p * net * period;
                return { l1, l2, l3, total: l1 + l2 + l3 };
            };

            const net9 = getNet(9.99);
            const net19 = getNet(19.99);
            const net29 = getNet(29.99);

            const avg = calc(averagePrice);

            return {
                model: model.label,
                period,
                levels: `${l1p * 100} / ${l2p * 100} / ${l3p * 100}`,
                users: `${structure.level1} / ${structure.level1 * structure.level2} / ${structure.level1 * structure.level2 * structure.level3}`,
                earnings: [calc(net9).total, calc(net19).total, calc(net29).total],
                average: avg.total
            };
        });

        const breakdowns = affiliateModels.map((model) => {
            const [l1p, l2p, l3p] = model.levels;
            const period = model.period;

            const calcDetail = (net) => {
                const l1 = structure.level1 * l1p * net * period;
                const l2 = structure.level1 * structure.level2 * l2p * net * period;
                const l3 = structure.level1 * structure.level2 * structure.level3 * l3p * net * period;
                return { l1, l2, l3, total: l1 + l2 + l3 };
            };

            const net9 = getNet(9.99);
            const net19 = getNet(19.99);
            const net29 = getNet(29.99);
            const netAvg = averagePrice;

            return {
                model: model.label,
                byPlan: [
                    { label: "$9.99", ...calcDetail(net9) },
                    { label: "$19.99", ...calcDetail(net19) },
                    { label: "$29.99", ...calcDetail(net29) },
                    { label: "Avg Mix", ...calcDetail(netAvg) }
                ]
            };
        });

        setResults({ modelSummaries, breakdowns });
    };

    const formatMoney = (value) => {
        const parts = value.toFixed(2).split(".");
        const whole = Number(parts[0]).toLocaleString("en-US").replace(/,/g, " ");
        return `$${whole}<sup style={{ fontSize: '0.65em' }}>.${parts[1]}</sup>`;
    };

    return (
        <div className="p-3 border rounded border-success">
            <h6 className="text-success mb-3">Affiliate Model Earnings Calculator</h6>

            <div className="text-muted mb-2">
                <strong>Affiliate Structure Setup:</strong><br />
                Specify how many users <em>you personally invite</em> (Level 1). Then, how many users <em>each of them invites</em> (Level 2), and finally how many users <em>each on Level 2 invites</em> (Level 3). This will help calculate your total referral network and expected earnings across all affiliate models.
            </div>

            <div className="d-flex flex-wrap gap-3 mb-3">
                {[
                    { key: "level1", label: "How many users YOU invite (Level 1)" },
                    { key: "level2", label: "Each L1 user invites (Level 2)" },
                    { key: "level3", label: "Each L2 user invites (Level 3)" }
                ].map(({ key, label }) => (
                    <Form.Group key={key}>
                        <Form.Label className="small text-muted mb-0">{label}</Form.Label>
                        <Form.Select size="sm" name={key} value={structure[key]} onChange={handleChange}>
                            {[5, 10, 20, 30, 40, 50, 100].map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                ))}

                <div className="d-flex align-items-end">
                    <Button size="sm" variant="success" onClick={calculate}>Calculate Earnings </Button>
                </div>
            </div>


            {results && (
                <>
                    <h6 className="text-muted small mb-2">Model Comparison Summary</h6>

                    <div className="table-responsive mb-3">
                        <Table striped bordered hover size="sm" className="small shadow-sm text-muted mb-0">
                            <thead>
                            <tr className="text-muted small text-nowrap">
                                <th>Model</th>
                                <th>Period</th>
                                <th>% Levels</th>
                                <th>Users</th>
                                <th>$9.99</th>
                                <th>$19.99</th>
                                <th>$29.99</th>
                                <th>Mixed Avg</th>
                            </tr>
                            </thead>
                            <tbody>
                            {results.modelSummaries.map((r) => (
                                <tr key={r.model} className="text-nowrap">
                                    <td>{r.model}</td>
                                    <td>{r.period} mo</td>
                                    <td>{r.levels}</td>
                                    <td>{r.users}</td>
                                    <td dangerouslySetInnerHTML={{ __html: `<strong>${formatMoney(r.earnings[0])}</strong>` }} />
                                    <td dangerouslySetInnerHTML={{ __html: `<strong>${formatMoney(r.earnings[1])}</strong>` }} />
                                    <td dangerouslySetInnerHTML={{ __html: `<strong>${formatMoney(r.earnings[2])}</strong>` }} />
                                    <td dangerouslySetInnerHTML={{ __html: `<strong>${formatMoney(r.average)}</strong>` }} />
                                </tr>
                            ))}
                            </tbody>
                        </Table>
                    </div>

                    {results.breakdowns.map((b) => (
                        <div key={b.model} className="mb-4">
                            <h6 className="text-muted small mb-2">Model: {b.model}</h6>

                            <div className="table-responsive">
                                <Table striped bordered hover size="sm" className="small shadow-sm text-muted mb-0">
                                    <thead>
                                    <tr className="text-nowrap">
                                        <th>Plan</th>
                                        <th>Level 1</th>
                                        <th>Level 2</th>
                                        <th>Level 3</th>
                                        <th>Total</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {b.byPlan.map((row) => (
                                        <tr key={row.label} className="text-nowrap">
                                            <td>{row.label}</td>
                                            <td dangerouslySetInnerHTML={{ __html: formatMoney(row.l1) }} />
                                            <td dangerouslySetInnerHTML={{ __html: formatMoney(row.l2) }} />
                                            <td dangerouslySetInnerHTML={{ __html: formatMoney(row.l3) }} />
                                            <td dangerouslySetInnerHTML={{ __html: `<strong>${formatMoney(row.total)}</strong>` }} />
                                        </tr>
                                    ))}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    ))}
                </>
            )}

        </div>
    );
};

export default AffiliateFullComparison;